import { Feature } from '@/constants/features';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import {
  useGetCrmOptionsQuery,
  useRemoveCrmConnectionMutation,
} from '@/store/features/crm.feature';
import { useOrganization, useUser } from '@clerk/react';
import { toast } from 'sonner';

/** `skip` holds the CRM options read for a caller that doesn't need it yet
 *  (the play builder's step picker with no CRM row). */
export const useCrm = (sfSandbox = false, { skip = false } = {}) => {
  const { user, isLoaded } = useUser();
  const { organization, isLoaded: isOrganizationLoaded } = useOrganization();
  const organizationId = organization?.id ?? '';
  const { hasFeature, isReady: isFeatureReady } = useFeatureAccess();
  const hasCrmAccess = isFeatureReady && hasFeature(Feature.CRM_INTEGRATIONS);
  const canFetch =
    !skip &&
    isLoaded &&
    isOrganizationLoaded &&
    Boolean(user) &&
    Boolean(organizationId) &&
    hasCrmAccess;
  const {
    currentData: crmOptions,
    isLoading: isCrmLoading,
    isFetching: isCrmFetching,
    error: crmError,
    refetch: refetchCrm,
  } = useGetCrmOptionsQuery({ sfSandbox, organizationId }, { skip: !canFetch });

  const [removeCrmConnectionMutation, { isLoading: isRemoving }] =
    useRemoveCrmConnectionMutation();

  const removeConnection = (connectionType: string) => {
    if (!organizationId) {
      toast.error('Select an organization before removing a CRM connection.');
      return;
    }
    removeCrmConnectionMutation({ connectionType, organizationId })
      .unwrap()
      .catch((error) => {
        toast.error(
          error.data?.detail || error.message || 'Failed to remove connection'
        );
      });
  };

  return {
    crmOptions,
    isCrmLoading,
    isCrmFetching,
    crmError,
    refetchCrm,
    removeConnection,
    isRemoving,
  };
};
